/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useState } from "react";
import { ResultActions } from "@/components/tools/result-actions";

const commonOverhead = [
  { label: "Rent / Shop Lease", placeholder: "2000" },
  { label: "Insurance (monthly)", placeholder: "500" },
  { label: "Vehicle Payments", placeholder: "800" },
  { label: "Tools & Equipment", placeholder: "300" },
  { label: "Phone / Internet / Software", placeholder: "200" },
  { label: "Marketing / Advertising", placeholder: "500" },
  { label: "Office / Admin Staff", placeholder: "0" },
  { label: "Other Fixed Costs", placeholder: "0" },
];

export function BreakEvenCalculatorClient() {
  const [overheadValues, setOverheadValues] = useState<string[]>(commonOverhead.map(() => ""));
  const [avgJobRevenue, setAvgJobRevenue] = useState("");
  const [avgJobCost, setAvgJobCost] = useState("");
  const [ownerSalary, setOwnerSalary] = useState("");
  const [targetProfit, setTargetProfit] = useState("");

  const totalOverhead = overheadValues.reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
  const osIn = ownerSalary;
  const tpIn = targetProfit;
  const salary = parseFloat(osIn) || 0;
  const target = parseFloat(tpIn) || 0;
  const totalFixed = totalOverhead + salary + target;

  const rIn = avgJobRevenue;
  const jIn = avgJobCost;
  const revenue = parseFloat(rIn) || 0;
  const cost = parseFloat(jIn) || 0;
  const jobProfit = revenue - cost;
  const jobMargin = revenue > 0 ? (jobProfit / revenue) * 100 : 0;

  // Break-even
  const breakEvenRevenue = jobMargin > 0 ? totalFixed / (jobMargin / 100) : 0;
  const breakEvenJobs = jobProfit > 0 ? Math.ceil(totalFixed / jobProfit) : 0;
  const breakEvenDaily = breakEvenRevenue / 22; // ~22 working days

  const hasResult = totalFixed > 0 && revenue > 0 && cost > 0 && jobProfit > 0;

  const fmt = (n: number) => n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const fmtDec = (n: number) => n.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  const inputClass = "mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary";

  const updateOverhead = (idx: number, value: string) => {
    const next = [...overheadValues];
    next[idx] = value;
    setOverheadValues(next);
  };

  return (
    <div className="mt-8 rounded-lg card-premium p-6">
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Left: Fixed costs */}
        <div>
          <h3 className="mb-3 text-sm font-semibold text-foreground">Monthly Fixed Costs</h3>
          <div className="grid gap-3">
            {commonOverhead.map((item, idx) => (
              <div key={item.label} className="flex items-center gap-3">
                <label className="w-48 flex-shrink-0 text-sm text-muted-foreground">{item.label}</label>
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10 pointer-events-none">$</span>
                  <input
                    type="number"
                    value={overheadValues[idx]}
                    onChange={(e) => updateOverhead(idx, e.target.value)}
                    placeholder={item.placeholder}
                    className="w-full rounded-md border border-input bg-background py-2 pl-7 pr-3 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                  />
                </div>
              </div>
            ))}
            <div className="flex items-center gap-3 border-t border-border pt-3">
              <label className="w-48 flex-shrink-0 text-sm font-semibold text-foreground">Owner&apos;s Salary</label>
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10 pointer-events-none">$</span>
                <input type="number" value={ownerSalary} onChange={(e) => setOwnerSalary(e.target.value)} placeholder="5000" className="w-full rounded-md border border-input bg-background py-2 pl-7 pr-3 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="w-48 flex-shrink-0 text-sm font-semibold text-foreground">Target Monthly Profit</label>
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10 pointer-events-none">$</span>
                <input type="number" value={targetProfit} onChange={(e) => setTargetProfit(e.target.value)} placeholder="3000" className="w-full rounded-md border border-input bg-background py-2 pl-7 pr-3 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-lg bg-muted/50 px-3 py-2">
              <span className="w-48 flex-shrink-0 text-sm font-semibold text-foreground">Total Monthly Nut</span>
              <span className="text-lg font-bold text-foreground">${fmt(totalFixed)}</span>
            </div>
          </div>
        </div>

        {/* Right: Job economics */}
        <div>
          <h3 className="mb-3 text-sm font-semibold text-foreground">Average Job Economics</h3>
          <div className="grid gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground">Avg Job Revenue ($)</label>
              <input type="number" value={avgJobRevenue} onChange={(e) => setAvgJobRevenue(e.target.value)} placeholder="5000" className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground">Avg Job Direct Costs ($)</label>
              <input type="number" value={avgJobCost} onChange={(e) => setAvgJobCost(e.target.value)} placeholder="3000" className={inputClass} />
              <p className="mt-1 text-xs text-muted-foreground">Materials + labor + subs (before overhead)</p>
            </div>
          </div>

          {revenue > 0 && cost > 0 && (
            <div className="mt-4 rounded-lg bg-muted/50 p-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-xs text-muted-foreground">Gross Profit</div>
                  <div className={`text-lg font-bold ${jobProfit > 0 ? "text-secondary" : "text-destructive"}`}>${fmt(jobProfit)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Gross Margin</div>
                  <div className={`text-lg font-bold ${jobMargin > 0 ? "text-secondary" : "text-destructive"}`}>{fmtDec(jobMargin)}%</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Markup</div>
                  <div className="text-lg font-bold text-foreground">{cost > 0 ? fmtDec((jobProfit / cost) * 100) : "0"}%</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {hasResult && (
        <div className="mt-6">
          <div className="grid gap-4 rounded-lg bg-muted/50 border border-border p-4 sm:grid-cols-4">
            <div>
              <div className="text-sm text-muted-foreground">Monthly Revenue Needed</div>
              <div className="text-2xl font-bold text-foreground">${fmt(Math.round(breakEvenRevenue))}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Jobs per Month</div>
              <div className="text-2xl font-bold text-foreground">{breakEvenJobs}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Daily Revenue Target</div>
              <div className="text-2xl font-bold text-foreground">${fmt(Math.round(breakEvenDaily))}</div>
              <div className="text-xs text-muted-foreground">~22 working days</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Annual Revenue Needed</div>
              <div className="text-2xl font-bold text-foreground">${fmt(Math.round(breakEvenRevenue * 12))}</div>
            </div>
          </div>
        </div>
      )}

      {hasResult && jobMargin < 20 && (
        <p className="mt-3 rounded-lg bg-secondary/10 border border-secondary/20 p-3 text-sm text-foreground">
          Your gross margin is under 20%. Most successful contractors target 35-50% gross margin to cover overhead and generate healthy profit.
        </p>
      )}

      {hasResult && (
        <ResultActions
          toolSlug="break-even-calculator"
          inputs={{ monthlyOverhead: totalOverhead, ownerSalary, targetProfit, avgJobRevenue, avgJobCost }}
          outputs={{ breakEvenRevenue, breakEvenJobs, breakEvenDaily, jobMargin }}
        />
      )}
    </div>
  );
}
